import {
  List,
  ListItem,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Box,
} from '@chakra-ui/react';
import { RefObject } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { ListingProperty, ListingProps } from '@/types/listing';
import { usePropertyTabs } from '../PropertyTabs';
import AttachmentItem, {
  AttachmentItemType,
} from '@/components/common/AttachmentItem';
import { urls } from '@/lib/urls';

const LinkListItem = ({
  attachment,
  type,
}: {
  attachment?: AttachmentItemType;
  type?: string;
}) => {
  return (
    <ListItem display="flex">
      <AttachmentItem attachment={attachment} type={type} />
    </ListItem>
  );
};

const AttachmentsList = ({
  property,
  salesBrochureAttachment,
}: {
  property?: ListingProperty;
  salesBrochureAttachment?: AttachmentItemType;
}) => {
  const { t } = useTranslation('frontoffice');

  if (
    !property?.property_attachment_publics.length &&
    !salesBrochureAttachment
  ) {
    return (
      <Box>{t('no_downloadable_content', 'Ei laddattavia aineistoja')}</Box>
    );
  }
  return (
    <List spacing={5}>
      <LinkListItem
        key="print"
        attachment={salesBrochureAttachment}
        type="printout"
      />
      {property?.property_attachment_publics.map(
        ({
          propertyAttachmentTypeByPropertyAttachmentType: attachmentType,
          attachment_public,
          id,
        }) => (
          <LinkListItem
            key={id}
            attachment={attachment_public}
            type={attachmentType.value}
          />
        ),
      )}
    </List>
  );
};

const AttachmentsBlock = ({
  listing,
  isPreview,
  sectionRef,
}: Pick<ListingProps, 'listing'> & {
  sectionRef: RefObject<HTMLDivElement>;
  isPreview?: boolean;
}) => {
  const { t } = useTranslation('frontoffice');
  const {
    query: { listing: listingId },
    locale = 'fi',
  } = useRouter();
  const { tabsProps, tabListProps, tabPanelsProps, tabPanelProps } =
    usePropertyTabs({
      sectionRef,
    });

  const salesBrochureAttachment = isPreview
    ? {
        url: urls.public.listing.brochure(String(listingId), locale || 'fi'),
        original_filename: t(
          'sales_brochure_preview',
          'Myyntiesitteen esikatselu',
        ),
        mime_type: 'application/pdf',
      }
    : {
        fi: listing.sales_brochure_attachment_fi?.[0]?.attachment_public,
        sv: listing.sales_brochure_attachment_sv?.[0]?.attachment_public,
        en: listing.sales_brochure_attachment_en?.[0]?.attachment_public,
      }[locale];

  // if we only have one property, don't show tabs
  if (listing.properties?.length < 2) {
    return (
      <AttachmentsList
        property={listing.properties?.[0]}
        salesBrochureAttachment={salesBrochureAttachment}
      />
    );
  }

  // otherwise wrap attachment lists in tabs
  return (
    <>
      <Tabs {...tabsProps} gridColumn="aside !important">
        <TabList {...tabListProps} />
      </Tabs>
      <Tabs {...tabsProps}>
        <TabPanels {...tabPanelsProps}>
          {listing.properties.map((property) => (
            <TabPanel {...tabPanelProps} key={property.id}>
              <AttachmentsList
                property={property}
                salesBrochureAttachment={salesBrochureAttachment}
              />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </>
  );
};

export default AttachmentsBlock;
